import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Close from '@material-ui/icons/Close';
import Done from '@material-ui/icons/Done';
import Replay from '@material-ui/icons/Replay';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import Refresh from '@material-ui/icons/Refresh';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Rating from '@material-ui/lab/Rating';
import firebase from 'firebase';
import { filter, isEmpty, size } from 'lodash';
import React, { FC } from 'react';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { Link as RouterLink } from "react-router-dom";
import { Player, RoundStatus, useGame, useGameContext, usePlayer, useRounds } from './hooks';
import Screen from './Screen';

type PlayerRefAvatarProps = {
  docRef: firebase.firestore.DocumentReference,
};

const PlayerRefAvatar: FC<PlayerRefAvatarProps> = ({ docRef }) => {
  const [player] = useDocumentData<Player>(docRef);
  return (
    <Avatar src={player?.avatar}>{player?.name}</Avatar>
  );
};

export const roundStatusIconMap = {
  [RoundStatus.COLLECTING_HINTS]: VisibilityOff,
  [RoundStatus.GUESSING]: Visibility,
  [RoundStatus.CORRECT]: Done,
  [RoundStatus.WRONG]: Close,
  [RoundStatus.NO_GUESS]: MoreHoriz,
};

type RoundListItemProps = {
  doc: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>,
};

const RoundListItem: FC<RoundListItemProps> = ({ doc }) => {
  const { gameId } = useGameContext();
  const { index, term, status, guess, guesser } = doc.data();
  const { player } = usePlayer();
  const isGuesser = guesser && player && guesser.isEqual(player.ref);
  const Icon = roundStatusIconMap[status as RoundStatus];
  return (
    <RouterLink to={`/room/game/${gameId}/round/${index}`}>
      <ListItem button>
        <Chip size="small" label={index} color="primary"/>
        <ListItemIcon>
          <Icon/>
        </ListItemIcon>
        {
        (status === RoundStatus.COLLECTING_HINTS || status === RoundStatus.GUESSING) && isGuesser ?
        <ListItemText/> :
        <ListItemText primary={term}/>
        }
        <PlayerRefAvatar docRef={guesser}/>
      </ListItem>
    </RouterLink>
  );
};

const getResult = (correctRounds: number) => {
  switch (correctRounds) {
    case 13: return 'Perfektes Ergebnis! Schafft ihr das noch mal?';
    case 12: return 'Unglaublich! Ihr seid Nua Oans vom perfekten Ergebnis entfernt.';
    case 11: return 'Genial! Dieses Ergebnis kann sich sehen lassen!';
    case 10:
    case 9: return 'Wow, ihr seid ein starkes Team!';
    case 8:
    case 7: return 'Ein beachtliches Ergebnis. Könnt ihr das noch besser?';
    case 6:
    case 5:
    case 4: return 'Das ist ein guter Anfang. Versucht es noch einmal!';
    default: return 'Übung macht den Meister.';
  }
}

export enum GameStatus {
  RUNNING = 'running',
  ENDED = 'ended',
}

export const Game = () => {
  const { rounds } = useRounds();
  const { game, createRound, canCreateRound, repeatRound, canRepeatRound, isFinished } = useGame();
  const { player, isRoomOwner } = usePlayer();
  if (!rounds || !player || !game) {
    return <CircularProgress />;
  }
  const correctRounds = size(filter(rounds?.docs, doc => doc.get('status') === RoundStatus.CORRECT));
  return (
    <Screen title="Game">
      <Card>
        <CardHeader
          title={`Spiel ${game.get('index')}`}
          subheader={
            game.get('status') === GameStatus.RUNNING ?
            'Dieses Spiel läuft gerade.' :
            'Dieses Spiel ist beendet.'
          }
        />
        {game.get('status') === GameStatus.ENDED &&
          <CardContent>
            <Rating max={13} value={correctRounds} readOnly/>
            <Typography>{getResult(correctRounds)}</Typography>
          </CardContent>
        }
        <CardContent>
        <Typography variant="h6">
            Runden
          </Typography>
          <Typography>
            Ein Spiel dauert 13 Runden.
          </Typography>
          {isEmpty(rounds.docs) ?
            <Typography>noch keine Runde gespielt</Typography> :
            <List>
              {rounds.docs.map((doc) => <RoundListItem key={doc.id} doc={doc}/>)}
            </List>
          }
        </CardContent>
        <CardActions>
          {isRoomOwner && game.get('status') === GameStatus.RUNNING && !isFinished &&
          <Button
            variant="contained"
            startIcon={<Refresh/>}
            disabled={!canCreateRound}
            onClick={createRound}
          >
            Neue Runde starten
          </Button>
          }
          {isRoomOwner &&
          <Button
            variant="contained"
            startIcon={<Replay/>}
            disabled={!canRepeatRound}
            onClick={repeatRound}
          >
            Runde wiederholen
          </Button>
          }
        </CardActions>
      </Card>
    </Screen>
  );
};

export default Game;
